import React, { useState } from "react";
import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip, Stack } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useFetch } from "./FetchProvider";
import { useRevalidator } from "react-router-dom";
import { useNotification } from "./NotificationProvider";

const EmailVerificationInfo: React.FC<{ data: any }> = ({ data }) => {
    const [resendInProgress, setResendInProgress] = useState(false);
    const [resendResult, setResendResult] = useState<boolean>(null);
    const fetch = useFetch();
    const revalidator = useRevalidator();
    const { showSuccess, showError } = useNotification();

    let verificationToken = null;
    for (let uet of data.tokens) {
        if (uet.type === "VERIFICATION") {
            verificationToken = uet;
        }
    }
    if (verificationToken == null) {
        return <span />;
    }
    const isWarn = resendResult == null ? !verificationToken.sent : !resendResult;

    const verifyEmail = async (userEmailId: number) => {
        const response = await fetch("/api/verify-email", {
            method: "POST",
            body: JSON.stringify({ token: userEmailId }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.ok) {
            showSuccess(
                <FormattedMessage id="user_email.email_verified" />,
                <FormattedMessage id="user_email.success" />,
            );
            revalidator.revalidate();
        } else {
            const result = await response.json();
            showError(result.reason, <FormattedMessage id="user_email.error" />);
        }
    };

    const resendEmailVerification = async (userEmailId: number) => {
        setResendInProgress(true);

        const response = await fetch("/api/resend-email-verification", {
            method: "POST",
            body: JSON.stringify({ id: userEmailId }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.ok) {
            showSuccess(
                <FormattedMessage id="user_email.email_has_been_sent" />,
                <FormattedMessage id="user_email.success" />,
            );
            setResendResult(true);
            setResendInProgress(false);
        } else {
            const result = await response.json();
            showError(result.reason, <FormattedMessage id="user_email.error" />);
            setResendResult(false);
            setResendInProgress(false);
        }
    };

    let feedbackLabel = <FormattedMessage id={isWarn ? "user_email.no_verif_sent" : "user_email.confirm_sent"} />;
    let verifyBtn = null;
    if (verificationToken.token)
        verifyBtn = (
            <Button
                onClick={() => {
                    verifyEmail(verificationToken.token);
                }}
                variant="contained"
                size="small"
            >
                <FormattedMessage id="user_email.verify" />
            </Button>
        );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={feedbackLabel} placement="bottom">
                <FontAwesomeIcon
                    icon={isWarn ? faExclamationTriangle : faInfoCircle}
                    color={isWarn ? "orange" : "green"}
                    style={{ margin: "0 10px" }}
                />
            </Tooltip>
            <Button
                variant="contained"
                size="small"
                disabled={resendInProgress}
                onClick={() => {
                    resendEmailVerification(verificationToken.id);
                }}
            >
                <FormattedMessage id={isWarn ? "user_email.retry" : "user_email.resend"} />
            </Button>
            {verifyBtn}
        </Stack>
    );
};

export default EmailVerificationInfo;
